/**
 * Phase 336 wave 1 task 1.3 (#1163) — the axis arithmetic shared by the chart
 * primitives.
 *
 * Internal to `charts/` (deliberately NOT exported from the package index): it is
 * one implementation of "round a scale up to a number a human would have chosen",
 * used by both {@link "./StackedBarChart.js"} and
 * {@link "./TrendSmallMultiple.js"}. Two copies of this arithmetic is how the
 * stacked chart and the small multiples end up disagreeing about what "£400,000"
 * means, so there is one copy.
 *
 * Pure, no IO, no React.
 */
/**
 * The clean multipliers an axis is allowed to land on. Excludes 3, 6, 7 and friends:
 * a tick ladder of 0 / 3,000 / 6,000 / 9,000 is arithmetically fine and cognitively
 * expensive — the reader has to do division to place a bar between two ticks.
 */
const STEP_LADDER = [1, 1.5, 2, 2.5, 5, 10];
/**
 * The smallest "clean" number ≥ `raw` — one of 1, 1.5, 2, 2.5, 5 or 10 times a power
 * of ten (17 → 20; 4,208 → 5,000; 0.42 → 0.5).
 *
 * Non-finite or non-positive input returns 1: an all-zero series still needs a
 * non-zero denominator, and a chart that divides by zero renders `NaN` coordinates,
 * which browsers drop silently — an invisible chart that looks like a data problem.
 */
export function niceStep(raw) {
    if (!Number.isFinite(raw) || raw <= 0)
        return 1;
    const exponent = Math.floor(Math.log10(raw));
    const base = Math.pow(10, exponent);
    const fraction = raw / base;
    for (const multiplier of STEP_LADDER) {
        // Epsilon guard: 100/10 is 10.000000000000002 in binary floating point, which
        // would otherwise skip a whole rung of the ladder.
        if (fraction <= multiplier + 1e-9)
            return multiplier * base;
    }
    return 10 * base;
}
/**
 * A y-axis: the ceiling plus every tick value, inclusive of 0 and the ceiling.
 *
 * The STEP is what gets rounded to a clean number (not the ceiling), so every tick
 * is a clean multiple of it — `max` is then `step × intervals`, which sits at or
 * above `rawMax` by construction and gives the tallest column a little headroom.
 *
 * @param rawMax The largest value that must fit inside the plot.
 * @param intervals How many gaps between ticks (4 → five tick labels).
 */
export function axisScale(rawMax, intervals = 4) {
    const count = Math.max(1, Math.floor(intervals));
    const step = niceStep(rawMax / count);
    const ticks = [];
    for (let i = 0; i <= count; i += 1) {
        // Multiply rather than accumulate: repeated addition of 2.5 drifts.
        ticks.push(step * i);
    }
    return { max: step * count, ticks };
}
/**
 * Rounds a coordinate to 2dp for the rendered attribute/path string. Full float
 * precision in a `d` attribute is a page-weight tax and makes a diff unreadable;
 * 2dp inside a 640-unit viewBox is far below a device pixel.
 */
export function round2(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=scale.js.map